
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db, auth } from '../../lib/firebase';
import { doc, onSnapshot, updateDoc, getDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { getSellerPlatformFeeRate, releaseEscrowToSeller } from '../../services/wallet';
import { orderDeliveredEmail } from '../../services/email';
import { getUserProfile } from '../../services/auth';

interface OrderData {
  id: string;
  buyerId: string;
  sellerId: string;
  status: 'payment_held' | 'delivery_started' | 'delivered' | 'completed' | 'cancelled';
  totalAmount: number;
  orderItems: { title?: string; image?: string; price?: number; quantity?: number }[];
  buyerName?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  deliveryAddress?: string;
  coordinates?: { lat: number; lng: number };
  shareLocation?: boolean;
  liveLocation?: { lat: number; lng: number; accuracy?: number; updatedAt?: any } | null;
  buyerNote?: string;
  sellerConfirmed?: boolean;
  buyerConfirmed?: boolean;
  createdAt?: any;
  buyerRatingOfSeller?: { rating: number; comment?: string; ratedAt?: any } | null;
  sellerRatingOfBuyer?: { rating: number; comment?: string; ratedAt?: any } | null;
}

const OrderDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState<OrderData | null>(null);
  const [userType, setUserType] = useState<'buyer' | 'seller' | null>(null);
  const [sellerProfile, setSellerProfile] = useState<any | null>(null);
  const [buyerProfile, setBuyerProfile] = useState<any | null>(null);
  const [processing, setProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [sharingLive, setSharingLive] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastWriteRef = useRef<number>(0);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingValue, setRatingValue] = useState<number>(5);
  const [ratingComment, setRatingComment] = useState<string>('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const autoOpenedRatingRef = useRef(false);

  // Real-time order listener
  useEffect(() => {
    if (!orderId) return;
    const unsub = onSnapshot(doc(db, 'orders', orderId), (snap) => {
      if (!snap.exists()) return;
      const o = snap.data() as any;
      setOrder({
        id: snap.id,
        buyerId: o.buyerId || '',
        sellerId: o.sellerId || '',
        status: o.status || 'payment_held',
        totalAmount: o.totalAmount || 0,
        orderItems: o.orderItems || [],
        buyerName: o.buyerName || '',
        buyerPhone: o.buyerPhone || '',
        buyerEmail: o.buyerEmail || '',
        deliveryAddress: o.deliveryAddress || '',
        coordinates: o.coordinates || null,
        shareLocation: !!o.shareLocation,
        liveLocation: o.liveLocation || null,
        buyerNote: o.buyerNote || '',
        sellerConfirmed: !!o.sellerConfirmed,
        buyerConfirmed: !!o.buyerConfirmed,
        createdAt: o.createdAt,
        buyerRatingOfSeller: o.buyerRatingOfSeller || null,
        sellerRatingOfBuyer: o.sellerRatingOfBuyer || null,
      });
    });
    return () => unsub();
  }, [orderId]);

  // Determine userType from Auth uid vs order ids
  useEffect(() => {
    if (!order) return;
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    if (uid === order.buyerId) setUserType('buyer');
    else if (uid === order.sellerId) setUserType('seller');
  }, [order]);

  // Load seller profile for buyer view
  useEffect(() => {
    if (!order?.sellerId || userType !== 'buyer') return;
    getDoc(doc(db, 'users', order.sellerId)).then(snap => {
      if (!snap.exists()) return;
      const s = snap.data() as any;
      setSellerProfile({
        name: s.fullName || s.name || 'Seller',
        phone: s.phone || '',
        university: s.university || '',
        studentNumber: s.studentNumber || '',
        bio: s.bio || '',
        photoURL: s.photoURL || '',
      });
    });
  }, [order?.sellerId, userType]);

  // Load buyer profile for seller view (fills in missing phone/email if not on order)
  useEffect(() => {
    if (!order?.buyerId || userType !== 'seller') return;
    getDoc(doc(db, 'users', order.buyerId)).then(snap => {
      if (!snap.exists()) return;
      const b = snap.data() as any;
      setBuyerProfile({
        name: b.fullName || b.name || '',
        phone: b.phone || '',
        email: b.email || '',
      });
    });
  }, [order?.buyerId, userType]);

  const stopSharingLive = () => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
    setSharingLive(false);
  };

  const startSharingLive = () => {
    setLiveError(null);
    if (!order) return;
    if (!navigator.geolocation) {
      setLiveError('Geolocation is not supported on this device.');
      return;
    }
    const id = navigator.geolocation.watchPosition(
      async (pos) => {
        const now = Date.now();
        if (now - lastWriteRef.current < 10000) return; // throttle writes to 10s
        lastWriteRef.current = now;
        try {
          await updateDoc(doc(db, 'orders', order.id), {
            liveLocation: {
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              updatedAt: serverTimestamp(),
            },
            shareLocation: true,
          });
        } catch (e) {
          console.error('Failed to update live location', e);
        }
      },
      (err) => {
        console.error('watchPosition error', err);
        setLiveError(err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Enable location access to share with the seller.'
          : 'Unable to get your location. Make sure location services are turned on.');
        stopSharingLive();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    watchIdRef.current = id;
    setSharingLive(true);
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const handleStartDelivery = async () => {
    if (!order) return;
    try {
      await updateDoc(doc(db, 'orders', order.id), { status: 'delivery_started' });
    } catch (err) {
      alert('Failed to start delivery. Please try again.');
    }
  };

  const handleMarkDelivered = async () => {
    if (!order) return;
    try {
      const confirmField = userType === 'seller' ? 'sellerConfirmed' : 'buyerConfirmed';
      await updateDoc(doc(db, 'orders', order.id), { [confirmField]: true });

      const snap = await getDoc(doc(db, 'orders', order.id));
      if (!snap.exists()) return;
      const data = snap.data() as any;
      const bothConfirmed = !!data.sellerConfirmed && !!data.buyerConfirmed;

      if (bothConfirmed) {
        await updateDoc(doc(db, 'orders', order.id), { status: 'delivered' });
        try {
          const sellerProf = await getUserProfile(order.sellerId);
          const buyerProf = await getUserProfile(order.buyerId);
          await orderDeliveredEmail(order.id, sellerProf?.email, buyerProf?.email);
        } catch {}
      }

      alert(bothConfirmed
        ? 'Both parties confirmed. Order marked as delivered!'
        : 'Your confirmation recorded. Waiting for the other party to confirm.'
      );
    } catch (err) {
      alert('Failed to confirm delivery. Please try again.');
    }
  };

  const handleReleasePayment = async () => {
    if (!order) return;
    const uid = auth.currentUser?.uid;
    if (!uid || uid !== order.buyerId) {
      alert('Only the buyer can release payment.');
      return;
    }
    if (!order.buyerConfirmed || !order.sellerConfirmed) {
      alert('Both buyer and seller must confirm delivery before releasing payment.');
      return;
    }
    try {
      setProcessing(true);
      const platformFeeRate = await getSellerPlatformFeeRate(order.sellerId);
      await releaseEscrowToSeller(order.sellerId, order.totalAmount, order.id, platformFeeRate);
      alert('Payment released to seller successfully!');
    } catch (err: any) {
      alert(err?.message || 'Failed to release payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Auto-open rating modal once, when the order becomes completed and the current role hasn't rated yet.
  useEffect(() => {
    if (!order || !userType) return;
    if (order.status !== 'completed') return;
    if (autoOpenedRatingRef.current) return;
    const alreadyRated = userType === 'buyer' ? !!order.buyerRatingOfSeller : !!order.sellerRatingOfBuyer;
    if (alreadyRated) return;
    autoOpenedRatingRef.current = true;
    setRatingValue(5);
    setRatingComment('');
    setShowRatingModal(true);
  }, [order?.status, userType, order?.buyerRatingOfSeller, order?.sellerRatingOfBuyer]);

  const handleSubmitRating = async () => {
    if (!order || !userType) return;
    const safeRating = Math.max(1, Math.min(5, Math.round(ratingValue)));
    const field = userType === 'buyer' ? 'buyerRatingOfSeller' : 'sellerRatingOfBuyer';
    try {
      setSubmittingRating(true);
      await updateDoc(doc(db, 'orders', order.id), {
        [field]: {
          rating: safeRating,
          comment: ratingComment.trim() || '',
          ratedAt: serverTimestamp(),
        },
      });

      // For buyer ratings, also publish a review doc so it shows on the seller's public page.
      if (userType === 'buyer') {
        const firstListingId = (order.orderItems as any[])?.find((i: any) => !!i?.listingId)?.listingId || '';
        const reviewerName = auth.currentUser?.displayName
          || buyerProfile?.name
          || order.buyerName
          || 'Buyer';
        try {
          await addDoc(collection(db, 'reviews'), {
            listingId: firstListingId,
            sellerId: order.sellerId,
            reviewerId: order.buyerId,
            reviewerName,
            rating: safeRating,
            comment: ratingComment.trim() || '',
            orderId: order.id,
            createdAt: serverTimestamp(),
          });
        } catch (e) {
          console.error('Rating saved, but failed to publish review', e);
        }
      }

      setShowRatingModal(false);
    } catch (e) {
      console.error('Failed to submit rating', e);
      alert('Failed to submit rating. Please try again.');
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) { alert('Please provide a reason for cancellation'); return; }
    try {
      await updateDoc(doc(db, 'orders', order!.id), { status: 'cancelled', cancelReason, cancelledAt: new Date() });
      setShowCancelModal(false);
      alert('Order cancelled. Our team will process your refund.');
    } catch {
      alert('Failed to cancel order. Please try again.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'payment_held': return 'bg-orange-100 text-orange-800';
      case 'delivery_started': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'payment_held': return 'Payment Held in Escrow';
      case 'delivery_started': return 'Delivery in Progress';
      case 'delivered': return 'Delivered — Awaiting Payment Release';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  if (!order || !userType) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  const primaryItem = order.orderItems?.[0];
  const buyerName = order.buyerName || buyerProfile?.name || 'N/A';
  const buyerPhone = order.buyerPhone || buyerProfile?.phone || '';
  const buyerEmail = order.buyerEmail || buyerProfile?.email || '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900">
              <i className="ri-arrow-left-line text-xl mr-2"></i>
              Back
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Order #{order.id.slice(-8).toUpperCase()}</h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-4">

        {/* Order Status */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base md:text-lg font-semibold text-gray-900">Order Status</h2>
            <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getStatusColor(order.status)}`}>
              {getStatusText(order.status)}
            </span>
          </div>
          {/* Progress Steps */}
          <div className="flex items-center gap-2 md:gap-4">
            {[
              { label: 'Payment Held', icon: 'ri-lock-line', done: true },
              { label: 'Delivery', icon: 'ri-truck-line', done: ['delivery_started', 'delivered', 'completed'].includes(order.status) },
              { label: 'Completed', icon: 'ri-check-double-line', done: order.status === 'completed' },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done ? 'bg-green-500' : 'bg-gray-300'}`}>
                    <i className={`${step.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-xs text-gray-500 mt-1 whitespace-nowrap">{step.label}</span>
                </div>
                {i < arr.length - 1 && (
                  <div className="flex-1 h-1 bg-gray-200 mb-4">
                    <div className={`h-full ${arr[i + 1].done ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Product */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Product Details</h2>
          <div className="flex items-center gap-4">
            {primaryItem?.image && (
              <img src={primaryItem.image} alt={primaryItem.title} className="w-16 h-16 md:w-20 md:h-20 object-cover rounded-lg" />
            )}
            <div>
              <p className="font-medium text-gray-900">{primaryItem?.title || 'Item'}</p>
              <p className="text-xl font-bold text-blue-600">₦{order.totalAmount.toLocaleString()}</p>
              <p className="text-xs text-gray-500">
                {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Seller sees: Buyer Info + Map */}
        {userType === 'seller' && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Buyer Information</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{buyerName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                {buyerPhone
                  ? <a href={`tel:${buyerPhone}`} className="text-blue-600 hover:underline">{buyerPhone}</a>
                  : <p className="text-gray-400">Not provided</p>
                }
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                {buyerEmail
                  ? <a href={`mailto:${buyerEmail}`} className="text-blue-600 hover:underline">{buyerEmail}</a>
                  : <p className="text-gray-400">Not provided</p>
                }
              </div>
              <div>
                <p className="text-xs text-gray-500">Delivery Address</p>
                <p className="text-gray-900">{order.deliveryAddress || 'Not provided'}</p>
              </div>
              {order.buyerNote && (
                <div className="sm:col-span-2">
                  <p className="text-xs text-gray-500">Buyer Note</p>
                  <p className="text-gray-900 bg-yellow-50 border border-yellow-200 rounded p-2 mt-1">{order.buyerNote}</p>
                </div>
              )}
            </div>

            {/* Buyer map location for seller (live if available, else static pin) */}
            {(() => {
              const live = order.liveLocation;
              const liveUpdatedMs = live?.updatedAt?.toMillis ? live.updatedAt.toMillis() : null;
              const liveFresh = liveUpdatedMs ? (Date.now() - liveUpdatedMs) < 5 * 60 * 1000 : false;
              const lat = liveFresh ? live!.lat : order.coordinates?.lat;
              const lng = liveFresh ? live!.lng : order.coordinates?.lng;
              if (lat && lng) {
                return (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                      {liveFresh ? (
                        <>
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
                          </span>
                          <span className="text-green-700 font-medium">Live location</span>
                          {liveUpdatedMs && (
                            <span className="text-gray-500">· updated {new Date(liveUpdatedMs).toLocaleTimeString()}</span>
                          )}
                        </>
                      ) : (
                        <>
                          <i className="ri-map-pin-line text-green-600"></i>
                          Buyer's delivery location
                        </>
                      )}
                    </p>
                    <div className="aspect-video rounded-lg overflow-hidden border border-gray-200">
                      <iframe
                        src={`https://www.google.com/maps?q=${lat},${lng}&z=16&output=embed`}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                    {liveFresh && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                      >
                        <i className="ri-navigation-line"></i>
                        Open directions
                      </a>
                    )}
                  </div>
                );
              }
              return (
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-500 flex items-center gap-2">
                    <i className="ri-map-pin-off-line"></i>
                    Buyer did not share their location. Use the delivery address above.
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* Buyer sees: Seller Info */}
        {userType === 'buyer' && sellerProfile && (
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
            <h2 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Seller Information</h2>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-100 border flex-shrink-0">
                {sellerProfile.photoURL
                  ? <img src={sellerProfile.photoURL} alt={sellerProfile.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-gray-400"><i className="ri-user-line text-2xl"></i></div>
                }
              </div>
              <div>
                <p className="font-semibold text-gray-900">{sellerProfile.name}</p>
                {sellerProfile.university && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <i className="ri-school-line"></i>{sellerProfile.university}
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {sellerProfile.phone && (
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-gray-400"></i>
                  <a href={`tel:${sellerProfile.phone}`} className="text-blue-600 hover:underline">{sellerProfile.phone}</a>
                </div>
              )}
              {sellerProfile.studentNumber && (
                <div className="flex items-center gap-3">
                  <i className="ri-id-card-line text-gray-400"></i>
                  <span className="text-gray-900">{sellerProfile.studentNumber}</span>
                </div>
              )}
              {sellerProfile.bio && (
                <div className="flex items-start gap-3">
                  <i className="ri-file-text-line text-gray-400 mt-0.5"></i>
                  <span className="text-gray-700 text-sm">{sellerProfile.bio}</span>
                </div>
              )}
            </div>

            {/* Your delivery address shown to buyer */}
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Your delivery address</p>
              <p className="text-sm text-gray-900">{order.deliveryAddress || 'Not set'}</p>
            </div>
          </div>
        )}

        {/* Confirmation status */}
        {['delivery_started', 'delivered'].includes(order.status) && (
          <div className="bg-white rounded-lg shadow-sm p-4">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Delivery Confirmation</h2>
            <div className="flex gap-4">
              <div className={`flex items-center gap-2 text-sm ${order.sellerConfirmed ? 'text-green-600' : 'text-gray-400'}`}>
                <i className={`ri-checkbox-${order.sellerConfirmed ? 'circle-fill' : 'blank-line'}`}></i>
                Seller confirmed
              </div>
              <div className={`flex items-center gap-2 text-sm ${order.buyerConfirmed ? 'text-green-600' : 'text-gray-400'}`}>
                <i className={`ri-checkbox-${order.buyerConfirmed ? 'circle-fill' : 'blank-line'}`}></i>
                Buyer confirmed
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Seller: start delivery */}
          {userType === 'seller' && order.status === 'payment_held' && (
            <button
              onClick={handleStartDelivery}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-truck-line text-xl"></i>
              Start Delivery
            </button>
          )}

          {/* Seller: mark delivered */}
          {userType === 'seller' && order.status === 'delivery_started' && !order.sellerConfirmed && (
            <button
              onClick={handleMarkDelivered}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-check-line text-xl"></i>
              Confirm Delivered
            </button>
          )}

          {/* Buyer: track delivery location */}
          {userType === 'buyer' && order.status === 'delivery_started' && (
            <button
              onClick={() => setShowLocationModal(true)}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-map-pin-line text-xl"></i>
              View My Delivery Location
            </button>
          )}

          {/* Buyer: share live location with seller */}
          {userType === 'buyer' && ['payment_held', 'delivery_started'].includes(order.status) && !order.buyerConfirmed && (
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 space-y-3">
              <div className="flex items-start gap-3">
                <i className="ri-radar-line text-2xl text-blue-600 mt-0.5"></i>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Share live location with seller</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Turn this on so the seller can see where you are in real time while they deliver. Keep this page open. Your location updates every 10 seconds.
                  </p>
                </div>
              </div>
              {liveError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
                  {liveError}
                </div>
              )}
              {!sharingLive ? (
                <button
                  onClick={startSharingLive}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-map-pin-user-line"></i>
                  Turn on live location
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600"></span>
                    </span>
                    Live location is being shared with the seller
                  </div>
                  <button
                    onClick={stopSharingLive}
                    className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <i className="ri-stop-circle-line"></i>
                    Stop sharing
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Buyer: confirm received */}
          {userType === 'buyer' && ['delivery_started', 'delivered'].includes(order.status) && !order.buyerConfirmed && (
            <button
              onClick={handleMarkDelivered}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-check-line text-xl"></i>
              I Received My Item
            </button>
          )}

          {/* Buyer: release payment */}
          {userType === 'buyer' && order.status === 'delivered' && order.buyerConfirmed && order.sellerConfirmed && (
            <button
              onClick={handleReleasePayment}
              disabled={processing}
              className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg hover:bg-purple-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-money-dollar-circle-line text-xl"></i>
              {processing ? 'Releasing...' : 'Release Payment to Seller'}
            </button>
          )}

          {/* Buyer: cancel */}
          {userType === 'buyer' && ['payment_held', 'delivery_started'].includes(order.status) && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="w-full border border-red-300 text-red-600 py-3 px-6 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-close-line text-xl"></i>
              Cancel Order
            </button>
          )}
        </div>

        {/* Escrow Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <i className="ri-shield-check-line text-blue-600 text-xl mt-0.5"></i>
            <div>
              <p className="font-semibold text-blue-800 text-sm">Escrow Protection Active</p>
              <p className="text-blue-700 text-sm mt-1">
                {userType === 'buyer'
                  ? 'Your payment is safely held. Only release it after you have physically received your item in good condition.'
                  : 'Payment will be released to you once the buyer confirms they received the item in good condition.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Buyer's delivery location modal (shown to buyer so they can confirm the pin is right) */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Your Delivery Location</h3>
              <button onClick={() => setShowLocationModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {order.coordinates?.lat && order.coordinates?.lng ? (
              <div className="aspect-video rounded-lg overflow-hidden mb-4">
                <iframe
                  src={`https://www.google.com/maps?q=${order.coordinates.lat},${order.coordinates.lng}&z=16&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            ) : (
              <div className="aspect-video rounded-lg bg-gray-100 flex items-center justify-center mb-4">
                <p className="text-gray-500 text-sm">No location pin available</p>
              </div>
            )}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700">
                <i className="ri-information-line mr-2"></i>
                This is the location the seller is delivering to. Your address: <strong>{order.deliveryAddress || 'Not set'}</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cancel Order</h3>
              <button onClick={() => setShowCancelModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for cancellation *</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please explain why you want to cancel..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">Our team will investigate and process the refund to your wallet.</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Keep Order
                </button>
                <button
                  onClick={handleCancelOrder}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Cancel Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal — auto-opens after order is completed */}
      {showRatingModal && userType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {userType === 'buyer' ? 'Rate the Seller' : 'Rate the Buyer'}
              </h3>
              <button onClick={() => setShowRatingModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {userType === 'buyer'
                ? 'How was your experience with this seller? Your rating helps other buyers.'
                : 'How was your experience with this buyer? Your rating helps other sellers.'}
            </p>
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRatingValue(star)}
                  className="text-3xl transition-transform hover:scale-110"
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <i className={star <= ratingValue ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-gray-300'}></i>
                </button>
              ))}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder={userType === 'buyer' ? 'Was the seller responsive, accurate, on time?' : 'Was the buyer communicative and cooperative?'}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRatingModal(false)}
                disabled={submittingRating}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-60"
              >
                Skip
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={submittingRating}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {submittingRating ? 'Submitting…' : 'Submit Rating'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetailsPage;
